import React, { useEffect, useState } from "react";
import Layouts from "../../components/Layouts";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Table, Typography, Card, Space, Row, Col, Skeleton } from "antd";
import { useSelector } from "react-redux";
import FacturasService from "../../services/facturas";

import { formatPrice } from "../../lib/utils";
import {
  getCajasByProducto,
  getPrimerSerialByCaja,
  getUltimoSerialByCaja,
  getTotalSerialesByCaja,
} from "../../services/seriales";

const { Title, Text } = Typography;

function VisualizarFactura(props) {
  let totalfinal = 0;
  const [producutosf, setProductosf] = useState([]);
  const [facturaData, setFacturaData] = useState(null);
  const [loading, setLoading] = useState(true);

  const signoMoneda = useSelector((state) => state.signoMoneda.signo_moneda);
  const location = useLocation();
  const navigate = useNavigate();

  const obtenerSerialesPorCaja = async (productoId) => {
    try {
      // Primero obtener todas las cajas únicas para este producto
      const cajasResponse = await getCajasByProducto(productoId);

      // Extraer cajas únicas
      const cajasUnicas = [...new Set(cajasResponse.map((item) => item.caja))];

      const resultadosPorCaja = [];

      for (const caja of cajasUnicas) {
        if (!caja) continue; // Saltar cajas nulas

        try {
          // 1. Obtener el primer serial (orden ascendente por id)
          const primerSerialResponse = await getPrimerSerialByCaja(caja);

          // 2. Obtener el último serial (orden descendente por id)
          const ultimoSerialResponse = await getUltimoSerialByCaja(caja);

          // 3. Contar total de seriales en esta caja
          const totalSerialesResponse = await getTotalSerialesByCaja(caja);

          if (
            primerSerialResponse.length > 0 &&
            ultimoSerialResponse.length > 0
          ) {
            const primerSerial = primerSerialResponse[0];
            const ultimoSerial = ultimoSerialResponse[0];
            const totalSeriales =
              totalSerialesResponse[0]?.countDistinct?.id || 0;

            // Extraer últimos 3 dígitos
            const primerSerialUltimos3 = primerSerial.serial.slice(-3);
            const ultimoSerialUltimos3 = ultimoSerial.serial.slice(-3);

            resultadosPorCaja.push({
              cajaId: caja,
              cajaNumero: caja,
              cantidadSeriales: totalSeriales,
              primerSerial: primerSerial.serial,
              ultimoSerial: ultimoSerial.serial,
              rangoInicio: primerSerialUltimos3,
              rangoFin: ultimoSerialUltimos3,
              rangoCompleto: `${primerSerialUltimos3} - ${ultimoSerialUltimos3}`,
            });
          }
        } catch (error) {
          console.error(`Error procesando caja ${caja}:`, error);
        }
      }

      return resultadosPorCaja;
    } catch (error) {
      console.error("Error fetching seriales por caja:", error);
      return [];
    }
  };

  const consulta = async () => {
    try {

      const productosPromises = facturaData.productos.map(async (producto) => {
        // Get seriales por caja for this producto
        const serialesPorCaja = await obtenerSerialesPorCaja(producto.id);

        return {
          nombre: producto.nombre,
          id: producto.id,
          cantidad: producto.cantidad,
          seriales: [], // Ya no necesitamos todos los seriales
          serialesPorCaja: serialesPorCaja,
          preciounitario: producto.precio_unitario,
          impuesto: producto.producto.grupo.impuesto,
        };
      });

      // Wait for all promises to resolve
      const productos = await Promise.all(productosPromises);
      return productos.filter(Boolean); // Remove any null values
    } catch (error) {
      console.error("Error fetching productos:", error);
      return [];
    }
  };

  useEffect(() => {
    const loadFacturaData = async () => {
      try {
        const facturaId = location.state.factura;
        const factura = await FacturasService.getFacturaDetails(facturaId);
        setFacturaData(factura);
      } catch (error) {
        console.error("Error loading factura data:", error);
        setLoading(false);
      }
    };

    if (location.state?.factura) {
      loadFacturaData();
    }
  }, [location.state]);

  useEffect(() => {
    const consultardata = async () => {
      try {
        if (facturaData && facturaData.productos) {
          const productos = await consulta();
          setProductosf(productos);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error in consultardata:", error);
        setProductosf([]);
        setLoading(false);
      }
    };

    if (facturaData) {
      consultardata();
    }
  }, [facturaData]);

  const columns = [
    {
      title: "Producto",
      dataIndex: "nombre",
      key: "nombre",
    },
    {
      title: "Cantidad",
      dataIndex: "cantidad",
      key: "cantidad",
    },
    {
      title: "Precio Unitario",
      dataIndex: "preciounitario",
      key: "preciounitario",
      render: (value) => `${formatPrice(value, signoMoneda)}`,
    },
    {
      title: "Impuesto",
      dataIndex: "impuesto",
      key: "impuesto",
      render: (value) => `${value}%`,
    },

    {
      title: "Total con impuesto",
      key: "total",
      render: (_, record) => {
        const total = record.cantidad * record.preciounitario;
        const impuesto = (total * record.impuesto) / 100;
        return `${formatPrice(total + impuesto, signoMoneda)}`;
      },
    },
  ];

  const calculateTotal = (data) => {
    return data.reduce(
      (acc, item) => acc + item.cantidad * item.preciounitario,
      0
    );
  };

  if (loading) {
    return (
      <Layouts>
        <Card>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Skeleton.Input style={{ width: 300, height: 32 }} active />
            
            <Space direction="vertical">
              <Skeleton.Input style={{ width: 200, height: 16 }} active />
              <Skeleton.Input style={{ width: 180, height: 16 }} active />
            </Space>

            {/* Skeleton para información de cajas */}
            <Card
              size="small"
              title={<Skeleton.Input style={{ width: 200, height: 16 }} active />}
              style={{ marginTop: 16 }}
            >
              <div style={{ marginBottom: 16 }}>
                <Skeleton.Input style={{ width: 150, height: 20 }} active />
                <div style={{ marginTop: 8 }}>
                  <Row gutter={[16, 16]}>
                    {[1, 2, 3].map((item) => (
                      <Col xs={24} sm={12} md={8} key={item}>
                        <div
                          style={{
                            padding: "8px 12px",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "6px",
                            border: "1px solid #d9d9d9",
                            height: "100px",
                          }}
                        >
                          <Skeleton active paragraph={{ rows: 3 }} />
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>
            </Card>

            {/* Skeleton para tabla */}
            <div>
              <Skeleton active paragraph={{ rows: 6 }} />
            </div>

            <Skeleton.Button style={{ width: 150 }} active />
          </Space>
        </Card>
      </Layouts>
    );
  }

  if (!facturaData) {
    return (
      <Layouts>
        <Card>
          <Text>Error al cargar los datos de la factura...</Text>
        </Card>
      </Layouts>
    );
  }

  return (
    <Layouts>
      <Card>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Title level={4}>Detalles de Factura</Title>

          <Space direction="vertical">
            <Text strong>Factura N°: {location.state.factura}</Text>
            <Text>
              Fecha:{" "}
              {new Date(facturaData.fecha_facturacion).toLocaleDateString()}
            </Text>

            {/* Información de Cajas y Rangos */}
            {producutosf.length > 0 && (
              <Card
                size="small"
                title="Información de Cajas y Rangos"
                style={{ marginTop: 16 }}
              >
                {producutosf.map((producto, index) => (
                  <div key={index} style={{ marginBottom: 16 }}>
                    <Text strong style={{ fontSize: "16px", color: "#20C77C" }}>
                      {producto.nombre}
                    </Text>
                    {producto.serialesPorCaja &&
                    producto.serialesPorCaja.length > 0 ? (
                      <div style={{ marginTop: 8 }}>
                        <Row gutter={[16, 16]}>
                          {producto.serialesPorCaja.map((caja, cajaIndex) => (
                            <Col xs={24} sm={12} md={8} key={cajaIndex}>
                              <div
                                style={{
                                  padding: "8px 12px",
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: "6px",
                                  border: "1px solid #d9d9d9",
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Text strong>Caja {caja.cajaNumero}</Text>
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {caja.cantidadSeriales} seriales
                                  </Text>
                                </div>
                                <div style={{ marginTop: 4 }}>
                                  <Text>
                                    Rango:{" "}
                                    <Text code>{caja.rangoCompleto}</Text>
                                  </Text>
                                </div>
                                <div
                                  style={{
                                    marginTop: 2,
                                    fontSize: "12px",
                                    color: "#666",
                                  }}
                                >
                                  <Text>
                                    Primer: {caja.primerSerial} | Último:{" "}
                                    {caja.ultimoSerial}
                                  </Text>
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    ) : (
                      <Text
                        type="secondary"
                        style={{ marginTop: 8, display: "block" }}
                      >
                        Sin seriales asignados
                      </Text>
                    )}
                  </div>
                ))}
              </Card>
            )}
          </Space>

          <Table
            columns={columns}
            dataSource={producutosf}
            pagination={false}
            rowKey="id"
            summary={(pageData) => {
              const total = calculateTotal(pageData);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <Text strong>Total sin impuesto</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <Text strong>{formatPrice(total, signoMoneda)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />

          <Button
            className="print-no"
            style={{ width: 150 }}
            onClick={() => navigate("/")}
          >
            Volver
          </Button>
        </Space>
      </Card>
    </Layouts>
  );
}

export default VisualizarFactura;
